import {m, useReducedMotion} from 'framer-motion'
import _ from 'lodash'
import {FC, useState} from 'react'

import {Button} from '@/components/common/content/Button'
import {Text, TextFontWeight, TextVariant} from '@/components/common/content/Text'
import {PlusCircleIcon} from '@/components/common/icons/PlusCircleIcon'
import {TextArea} from '@/components/inputs/TextArea'
import {getFormContent} from '@/content/form'
import {useTranslation} from '@/i18n'
import {EXPAND_TRANSITION} from '@/theme/cssTransitions'
import {FORM_TEXTAREA_MAX_HEIGHT} from '@/theme/sizing'
import {theme} from '@/theme/theme'

import {FormItem} from './FormItem'
import {FormSection} from './FormSection'

type FormContactNoteProps = {name: string; isCollapsible?: boolean}

export const FormContactNote: FC<FormContactNoteProps> = ({name, isCollapsible = false}) => {
  const {t} = useTranslation()
  const {title, description, optionalLabel, placeholder, returningClientButtonText} =
    getFormContent().contactNote
  const [isNoteVisible, setIsNoteVisible] = useState(false)
  const shouldReduceMotion = useReducedMotion()

  const textArea = (
    <TextArea
      name={name}
      numberOfRows={2}
      autosize
      maxHeight={FORM_TEXTAREA_MAX_HEIGHT}
      placeholder={t(placeholder)}
    />
  )

  return (
    <>
      {isCollapsible ? (
        <div className="wrapper">
          <Button
            type="button"
            icon={<PlusCircleIcon />}
            iconPosition="left"
            textColor={theme.palette.text.default}
            textVariant={TextVariant.text14}
            fontWeight={TextFontWeight.medium}
            onClick={() => setIsNoteVisible(!isNoteVisible)}
            textAlign="left"
          >
            {t(returningClientButtonText)}
          </Button>
          <m.div
            initial={false}
            animate={{
              height: isNoteVisible ? 'auto' : 0,
              opacity: isNoteVisible ? 1 : 0,
              marginTop: isNoteVisible ? theme.spacing[3] : 0,
            }}
            transition={{
              duration: shouldReduceMotion ? 0 : EXPAND_TRANSITION / 1000,
              ease: 'easeInOut',
            }}
            onUpdate={_.noop}
          >
            <FormItem label={t(optionalLabel)}>{textArea}</FormItem>
          </m.div>
        </div>
      ) : (
        <FormSection
          title={t(title)}
          description={<Text variant={TextVariant.text14}>{t(description)}</Text>}
        >
          <FormItem label={t(optionalLabel)}>{textArea}</FormItem>
        </FormSection>
      )}

      <style jsx>{`
        .wrapper {
          margin-top: ${theme.spacing[4]};
        }
      `}</style>
    </>
  )
}
